import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  ShoppingCart, Package, DollarSign, 
  ArrowLeft, Send, Loader2, Plus, Trash2
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export const NewOrder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cartId = searchParams.get('cart');
  
  const [loading, setLoading] = useState(!!cartId);
  const [submitting, setSubmitting] = useState(false);
  const [cartData, setCartData] = useState(null);
  const [notes, setNotes] = useState("");
  const [manualItems, setManualItems] = useState([]);
  const [newItem, setNewItem] = useState({ name: "", quantity: 1, unit_price: 0 });
  const [shippingAddress, setShippingAddress] = useState({
    name: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "Haiti",
    phone: ""
  });

  const token = localStorage.getItem('enterprise_token');

  useEffect(() => {
    if (!token) {
      navigate('/enterprise');
      return;
    }
    
    if (cartId) {
      fetchCart();
    }
  }, [token, cartId, navigate]);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/enterprise/punchout/cart/${cartId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.session?.cart_data) {
        setCartData(response.data.session.cart_data);
      } else {
        toast.info("Pa gen done panye Amazon. Ou ka ajoute atik manyèlman.");
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      toast.info("Pa jwenn panye Amazon. Ou ka kreye kòmand manyèlman.");
    } finally {
      setLoading(false);
    }
  };

  const addManualItem = () => {
    if (!newItem.name || newItem.unit_price <= 0) {
      toast.error("Tanpri ranpli non ak pri pwodwi a");
      return;
    }
    
    const item = {
      ...newItem,
      total: newItem.quantity * newItem.unit_price,
      asin: `MANUAL-${Date.now()}`
    };
    
    setManualItems([...manualItems, item]);
    setNewItem({ name: "", quantity: 1, unit_price: 0 });
  };

  const removeManualItem = (index) => {
    setManualItems(manualItems.filter((_, i) => i !== index));
  };

  const getAllItems = () => {
    const amazonItems = cartData?.items || [];
    return [...amazonItems, ...manualItems];
  };

  const getSubtotal = () => {
    return getAllItems().reduce((sum, item) => sum + (item.total || item.quantity * item.unit_price), 0);
  };

  const handleSubmitOrder = async () => {
    const items = getAllItems();
    
    if (items.length === 0) {
      toast.error("Ajoute omwen yon atik nan kòmand la");
      return;
    }

    // Validate shipping address
    if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city) {
      toast.error("Tanpri ranpli adrès livrezon an (Non, Ri, Vil)");
      return;
    }

    // Double check - if already submitting, don't proceed
    if (submitting) {
      console.log("Already submitting, ignoring duplicate request");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/enterprise/orders`,
        {
          items: items,
          notes: notes,
          shipping_address: shippingAddress
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // Check if this was a duplicate
        if (response.data.message === "Kòmand deja egziste") {
          toast.info("Kòmand deja kreye");
        } else {
          toast.success("Kòmand kreye ak siksè!");
        }
        navigate(`/enterprise/orders`);
      }
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(error.response?.data?.detail || "Erè nan kreye kòmand");
      setSubmitting(false); // Only reset on error
    }
    // Don't reset submitting on success - we're navigating away
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const allItems = getAllItems();
  const subtotal = getSubtotal();

  return (
    <EnterpriseLayout>
      <div className="space-y-6" data-testid="new-order-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/enterprise/dashboard')}
            className="p-2"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#232F3E]">Nouvo Kòmand</h1>
            <p className="text-gray-500">Ajoute atik ak soumèt kòmand ou a</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF9900]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Add Items + Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {/* Manual Item Entry */}
              <Card className="border-0 shadow-lg border-l-4 border-l-[#FF9900]">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plus className="w-5 h-5 text-[#FF9900]" />
                    Ajoute Atik
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-2">
                      <Label>Non Pwodwi</Label>
                      <Input
                        placeholder="Ex: Laptop Dell XPS 15"
                        value={newItem.name}
                        onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                        data-testid="item-name"
                      />
                    </div>
                    <div>
                      <Label>Kantite</Label>
                      <Input
                        type="number"
                        min={1}
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({...newItem, quantity: parseInt(e.target.value) || 1})}
                        data-testid="item-quantity"
                      />
                    </div>
                    <div>
                      <Label>Pri (USD)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem({...newItem, unit_price: parseFloat(e.target.value) || 0})}
                        data-testid="item-price"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={addManualItem}
                    className="mt-4 bg-[#FF9900] hover:bg-[#F08C00] text-black"
                    data-testid="add-item-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajoute Atik
                  </Button>
                </CardContent>
              </Card>

              {/* Items List */}
              {allItems.length > 0 && (
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5 text-[#FF9900]" />
                      Atik yo ({allItems.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {allItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl"
                        data-testid={`cart-item-${index}`}
                      >
                        <div className="w-16 h-16 bg-white rounded-lg flex items-center justify-center border">
                          <Package className="w-8 h-8 text-gray-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#232F3E] line-clamp-2">
                            {item.description || item.name || 'Pwodwi'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {item.asin?.startsWith('MANUAL') ? 'Ajoute manyèlman' : `ASIN: ${item.supplier_part_id || item.asin || 'N/A'}`}
                          </p>
                          <div className="flex items-center gap-4 mt-2">
                            <span className="text-sm text-gray-600">
                              Kantite: {item.quantity}
                            </span>
                            <span className="text-sm text-gray-600">
                              Pri inite: {formatCurrency(item.unit_price)}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex items-start gap-2">
                          <p className="text-lg font-bold text-[#FF9900]">
                            {formatCurrency(item.total || (item.quantity * item.unit_price))}
                          </p>
                          {item.asin?.startsWith('MANUAL') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeManualItem(manualItems.indexOf(item))}
                              className="text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Shipping Address */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-[#FF9900]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    Adrès Livrezon
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>Non Konplè *</Label>
                      <Input
                        placeholder="Non moun ki pral resevwa kòmand la"
                        value={shippingAddress.name}
                        onChange={(e) => setShippingAddress({...shippingAddress, name: e.target.value})}
                        data-testid="shipping-name"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Adrès (Ri) *</Label>
                      <Input
                        placeholder="123 Ri Prensipal"
                        value={shippingAddress.street}
                        onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                        data-testid="shipping-street"
                      />
                    </div>
                    <div>
                      <Label>Vil *</Label>
                      <Input
                        placeholder="Pòtoprens"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                        data-testid="shipping-city"
                      />
                    </div>
                    <div>
                      <Label>Depatman/Eta</Label>
                      <Input
                        placeholder="Ouest"
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                        data-testid="shipping-state"
                      />
                    </div>
                    <div>
                      <Label>Kòd Postal</Label>
                      <Input
                        placeholder="HT6110"
                        value={shippingAddress.zip}
                        onChange={(e) => setShippingAddress({...shippingAddress, zip: e.target.value})}
                        data-testid="shipping-zip"
                      />
                    </div>
                    <div>
                      <Label>Peyi</Label>
                      <Input
                        value={shippingAddress.country}
                        onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                        data-testid="shipping-country"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Telefòn</Label>
                      <Input
                        placeholder="+509 1234 5678"
                        value={shippingAddress.phone}
                        onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                        data-testid="shipping-phone"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle>Nòt (Opsyonèl)</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="Ajoute nenpòt nòt oswa enstriksyon espesyal pou kòmand sa a..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    data-testid="order-notes"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="border-0 shadow-lg sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-[#FF9900]" />
                    Rezime Kòmand
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Atik</span>
                      <span className="font-medium">{allItems.length}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold">Total USD</span>
                      <span className="font-bold text-[#FF9900]">
                        {formatCurrency(subtotal)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-500">
                      <span>Total HTG</span>
                      <span>
                        {new Intl.NumberFormat('fr-HT').format(subtotal * 148)} HTG
                      </span>
                    </div>
                  </div>

                  <div className="pt-4 space-y-3">
                    <Button
                      onClick={handleSubmitOrder}
                      disabled={submitting || allItems.length === 0}
                      className="w-full bg-[#FF9900] hover:bg-[#F08C00] text-black font-bold py-6 disabled:opacity-50"
                      data-testid="submit-order-btn"
                    >
                      {submitting ? (
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      ) : (
                        <Send className="w-5 h-5 mr-2" />
                      )}
                      Soumèt Kòmand
                    </Button>
                    
                    <p className="text-xs text-center text-gray-500">
                      Kòmand la ap bezwen apwobasyon anvan peman
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </EnterpriseLayout>
  );
};

